import React, { Suspense, useEffect, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF, OrbitControls } from '@react-three/drei';
import { Item } from './styles';
import { formatTimestamp, formatValue } from './utils';
import { getSignedUrl } from "../../../server";

const DEG = 180 / Math.PI;

function quaternionToEuler(w, x, y, z) {
  const roll = Math.atan2(2 * (w * x + y * z), 1 - 2 * (x * x + y * y)) * DEG;
  const sinp = 2 * (w * y - z * x);
  const pitch = Math.abs(sinp) >= 1 ? Math.sign(sinp) * 90 : Math.asin(sinp) * DEG;
  const yaw = Math.atan2(2 * (w * z + x * y), 1 - 2 * (y * y + z * z)) * DEG;
  return { roll, pitch, yaw };
}

// Three.js quaternion order (x, y, z, w); yaw is not observable from gravity
// alone, so tilt-only orientations leave it at zero.
function tiltToQuaternion(roll, pitch) {
  const cr = Math.cos(roll / 2), sr = Math.sin(roll / 2);
  const cp = Math.cos(pitch / 2), sp = Math.sin(pitch / 2);
  return [sr * cp, cr * sp, -sr * sp, cr * cp];
}

// The fusion IMU publishes a quaternion; denshi publishes raw accel/gyro only,
// so its tilt is read off the gravity vector instead. That reads the WEC's
// own acceleration as tilt too, which is acceptable for a live view.
function orientation(d) {
  if (d.quat_real != null) {
    return {
      ...quaternionToEuler(d.quat_real, d.quat_i, d.quat_j, d.quat_k),
      quaternion: [d.quat_i, d.quat_j, d.quat_k, d.quat_real],
    };
  }
  if (d.accel_z != null) {
    const roll = Math.atan2(d.accel_y, d.accel_z);
    const pitch = Math.atan2(-d.accel_x, Math.hypot(d.accel_y, d.accel_z));
    return { roll: roll * DEG, pitch: pitch * DEG, quaternion: tiltToQuaternion(roll, pitch) };
  }
  return null;
}

// Readings arrive at 1 Hz. The model is drawn LAG seconds behind, so each
// frame sits between buffered samples and the orientation is rebuilt from them
// by sinc interpolation under a Lanczos window - the band-limited curve through
// the samples, rather than a chase toward the newest one.
const PERIOD = 1, LOBES = 2, LAG = LOBES * PERIOD;
const sinc = (x) => (x === 0 ? 1 : Math.sin(Math.PI * x) / (Math.PI * x));
const lanczos = (x) => (Math.abs(x) < LOBES ? sinc(x) * sinc(x / LOBES) : 0);

function Model({ url, stamp, quaternion }) {
  const { scene } = useGLTF(url);
  const ref = useRef();
  const samples = useRef([]);

  useEffect(() => {
    const prev = samples.current.at(-1)?.q;
    // q and -q are the same rotation; keep the sign continuous so they blend.
    const flip = prev && prev.reduce((acc, v, i) => acc + v * quaternion[i], 0) < 0;
    const q = flip ? quaternion.map((v) => -v) : quaternion;
    samples.current = [...samples.current.slice(-7), { t: performance.now() / 1000, q }];
  }, [stamp]); // eslint-disable-line react-hooks/exhaustive-deps

  useFrame(() => {
    const t = performance.now() / 1000 - LAG;
    const sum = [0, 0, 0, 0];
    let norm = 0;
    for (const { t: tk, q } of samples.current) {
      const w = lanczos((t - tk) / PERIOD);
      norm += w;
      q.forEach((v, i) => { sum[i] += w * v; });
    }
    if (norm > 1e-3) ref.current?.quaternion.set(...sum.map((v) => v / norm)).normalize();
  });

  return <primitive ref={ref} object={scene} scale={6} position={[0, 0, 0.5]} />;
}

function IMUCanvas({ stamp, quaternion }) {
  const [signedUrl, setSignedUrl] = React.useState(null);

  useEffect(() => {
    (async function () {
      const signedUrl = await getSignedUrl("kaizen-model.gltf");
      setSignedUrl(signedUrl);
    })();
  }, []);

  if (!signedUrl) return null;

  return (
    <div style={{ width: '100%', height: 200, borderRadius: 8, overflow: 'hidden', marginTop: '1em' }}>
      <Canvas camera={{ position: [-1.50, -2.70, 0.83], up: [0, 0, 1], fov: 45 }}>
        <ambientLight intensity={0.6} />
        <directionalLight position={[5, 5, 5]} intensity={1} />
        <mesh position={[0, 0, 0.4]}>
          <planeGeometry args={[100, 100]} />
          <meshStandardMaterial color="#00aaff" transparent opacity={0.4} />
        </mesh>
        <Suspense fallback={null}>
          <Model url={signedUrl} stamp={stamp} quaternion={quaternion} />
        </Suspense>
        <OrbitControls 
          enableZoom={false} 
          maxPolarAngle={Math.PI / 2.3}
          minPolarAngle={Math.PI / 2.3}
        />
      </Canvas>
    </div>
  );
}

export default function IMU({ data }) {
  if (!data) return null;

  const entries = Object.entries(data);
  // Use the first sensor for the 3D view
  const [, first] = entries[0] ?? [];
  const firstOrientation = first && orientation(first);

  return (
    <Item>
      <h3 style={{ marginBlockEnd: '0.5em' }}>IMU</h3>
      {entries.map(([sensor_id, d]) => {
        const { roll, pitch, yaw } = orientation(d) ?? {};
        return (
          <div key={d.id || d.timestamp} style={{ marginTop: '0.5em' }}>
            {sensor_id || d.sensor_id} - {formatTimestamp(d.timestamp)}:
            <p style={{ marginLeft: '1em', marginBottom: 0 }}>
              Roll: {formatValue(roll, 1)} °,
              Pitch: {formatValue(pitch, 1)} °,
              Yaw: {formatValue(yaw, 1)} °
            </p>
            {(d.accel_x != null || d.accel_y != null || d.accel_z != null) && (
              <p style={{ marginLeft: '1em', marginBottom: 0 }}>
                Accel: {formatValue(d.accel_x, 2)}, {formatValue(d.accel_y, 2)}, {formatValue(d.accel_z, 2)} m/s²
              </p>
            )}
            {(d.gyro_x != null || d.gyro_y != null || d.gyro_z != null) && (
              <p style={{ marginLeft: '1em' }}>
                Gyro: {formatValue(d.gyro_x, 2)}, {formatValue(d.gyro_y, 2)}, {formatValue(d.gyro_z, 2)} rad/s
              </p>
            )}
          </div>
        );
      })}
      {firstOrientation && <IMUCanvas stamp={first.timestamp} quaternion={firstOrientation.quaternion} />}
    </Item>
  );
}
